import { CSSObject } from "@emotion/react";
import { IonText, useIonRouter } from "@ionic/react";
import { useAuth } from "pages";
import { IoEllipsisHorizontal } from "react-icons/io5";
import { Link } from "react-router-dom";
import { createMorePath, SIDE_MENU_TEST_ID_PREFIX, sideMenuItemsTop } from "routes/routeData";
import { subPhotoPlaceholderPNG, swingLogo130, swingLogomarkWhite } from "swing-assets";
import { Colors, Icon } from "swing-components";
import { getBaseURLFromRouter, openHelpCenter } from "swing-utils";
import { filterMenuItemsForUserSubset, truncateString } from "utils/helpers";
import { useAdminSecurityRestrictions, useIntercomMessenger } from "utils/hooks";

import { translate } from "~utils";

import SideMenuItem from "./SideMenuItem";

type DesktopSideMenuProps = {
  expanded: boolean;
  flipExpandedState: () => void;
};

export function DesktopSideMenu({ expanded, flipExpandedState }: DesktopSideMenuProps) {
  const { authInfo, userInfo } = useAuth();
  const securityRestrictions = useAdminSecurityRestrictions();

  const router = useIonRouter();
  const currentBaseRoute = `/${getBaseURLFromRouter(router)}`;

  const { openIntercomMessenger } = useIntercomMessenger();

  return (
    <div
      css={{
        backgroundColor: "var(--swing-blue-100)",
        overflow: "visible",
        zIndex: 102,
        display: "flex",
        transition: "all 0.2s ease",
        flexDirection: "column",
      }}
    >
      <div
        css={{
          backgroundColor: "var(--swing-blue-100)",
          position: "relative",
          overflow: "visible",
          width: "100%",
          zIndex: 102,
          padding: "32px 0px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
        }}
      >
        <div
          css={{
            backgroundColor: "inherit",
            height: 32,
            width: 32,
            display: "flex",
            justifyContent: "center",
            borderRadius: "50%",
            alignItems: "center",
            position: "absolute",
            filter: "drop-shadow(0px 4px 8px rgba(24, 36, 56, 0.6))",
            right: -16,
            top: 32,
            cursor: "pointer",
            rotate: expanded ? "180deg" : "0deg",
          }}
          onClick={flipExpandedState}
        >
          <Icon
            name="Chevron Right"
            color={Colors.white200}
            data-testid={`${SIDE_MENU_TEST_ID_PREFIX}-toggle_menu`}
          />
        </div>
        <Link to="/">
          <img
            src={expanded ? swingLogo130 : swingLogomarkWhite}
            alt=""
            css={{
              marginBottom: 50,
            }}
          />
        </Link>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <div css={styles.menuItemsTop}>
            {sideMenuItemsTop
              .filter((item) => filterMenuItemsForUserSubset(item, securityRestrictions))
              .map((item, index) => {
                return (
                  <SideMenuItem
                    text={item.text}
                    image={<item.logo className="icon-2xl" />}
                    linkUrl={item.linkUrl}
                    expanded={expanded}
                    isSelected={currentBaseRoute === item.linkUrl}
                    key={index}
                    testId={item.testId}
                  />
                );
              })}
          </div>
          <div css={styles.menuItemsTop}>
            <SideMenuItem
              text={translate("HELP_CENTER")}
              image={<Icon name="Help" color={Colors.white200} />}
              linkUrl={openHelpCenter}
              expanded={expanded}
              testId={`${SIDE_MENU_TEST_ID_PREFIX}-help_center_button`}
            />
            <SideMenuItem
              text={translate("SWING_SUPPORT")}
              image={<Icon name="Chat Bubble" color={Colors.white200} />}
              linkUrl={openIntercomMessenger}
              expanded={expanded}
              testId={`${SIDE_MENU_TEST_ID_PREFIX}-swing_support_button`}
            />
            <SideMenuItem
              text={translate("ROUTE_TABLE_MORE")}
              image={<IoEllipsisHorizontal className="icon-2xl" />}
              linkUrl={createMorePath()}
              expanded={expanded}
              isSelected={currentBaseRoute === createMorePath()}
              testId={`${SIDE_MENU_TEST_ID_PREFIX}-sign_out_button`}
            />
          </div>
        </div>
      </div>
      <div
        css={{
          display: "flex",
          gap: 9,
          alignItems: "center",
          backgroundColor: "var(--swing-blue-200)",
          padding: "24px 18px",
          justifyContent: "center",
          color: "var(--swing-gray-200)",
          fontWeight: 500,
          textDecoration: "none",
          fontSize: 14,
        }}
      >
        <img
          src={authInfo.provider === "google" ? authInfo.profilePhoto : subPhotoPlaceholderPNG}
          css={{
            width: 24,
            height: 24,
            objectFit: "contain",
          }}
          alt={userInfo.name}
        />
        {expanded && <IonText>{truncateString(userInfo.name, 12)}</IonText>}
      </div>
    </div>
  );
}

const styles: Record<string, CSSObject> = {
  menuItemsTop: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
};
